import React, { useEffect, useState } from 'react'
import { useI18n } from '@ggs/i18n'
import { Box, Link } from '@mui/material'
import { DataTable } from '../'
import { Loader } from '../../loader'
import { useAcademicDataDigest } from '../../../local'
import { size } from 'lodash'
import { useAcademicApi } from '../../../local/hooks'
import { useSelector } from '@ggs/store'
import './ProgramStudiesTable.scss'

/**
 * @typedef {import('../../../definitions').StandardTimetableListResponse} StandardTimetableListResponse
 * @typedef {import('mui-datatables').MUIDataTableOptions} MUIDataTableOptions
 */

/**
 * Program studies table.
 * @return {JSX.Element}
 */
const ProgramStudiesTable = () => {
  const { t } = useI18n()
  const { getStandardTimetables } = useAcademicApi()
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'
  const [dataTable, setDataTable] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const { digestProgramStudyList } = useAcademicDataDigest()
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)

  const pageSize = 500
  // Rows presented for each table page.
  const rowsPerPage = 50
  const enableLoading = () => {
    setLoading(true)
  }

  const disableLoading = () => {
    setLoading(false)
  }

  /**
   * Receive current page index.
   * @param {number} currentPage
   */
  const onPageChange = (currentPage) => {
    setCurrentPageIndex(currentPage)
  }

  /**
   * Handle click on subject title.
   * @param {React.MouseEvent<HTMLAnchorElement>} e
   */
  const handleLinkClick = (e) => {
    // Prevent opening target link when running in SB.
    if (isStorybook) {
      e.preventDefault()
      alert('Program of study detail route will open when running in Drupal.')
    }
  }

  /**
   * Resolve programs of study / academic levels data.
   */
  const resolveSubjectsData = async () => {
    enableLoading()

    const response = await getStandardTimetables({
      pageSize,
      academicPeriodId: [academicPeriod],
      status: 'Published',
    })

    const pageDataTable = digestProgramStudyList(response.pageItems)
    if (pageDataTable) {
      setDataTable(pageDataTable)
    }

    disableLoading()
  }

  /**
   * @type {MUIDataTableOptions}
   */
  const options = {
    page: currentPageIndex,
    onChangePage: onPageChange,
    jumpToPage: false,
    pagination: true,
    sortOrder: {
      name: 'programStudyTitle',
      direction: 'asc'
    },
    rowsPerPage,
    textLabels: {
      body: {
        noMatch: loading ? t('Please wait...') : t('No results found'),
      },
    },
  }

  useEffect(() => {
    resolveSubjectsData().then(() => {})
  }, [academicPeriod])

  if (loading) {
    return <Loader />
  }

  /**
   * Custom render function for program study title column
   * @param {number} dataIndex
   * @return {JSX.Element}
   */
  const renderTitleLink = (dataIndex) => {
    const id = dataTable[dataIndex].id
    const value = dataTable[dataIndex].programStudyTitle
    return (
      <Link onClick={handleLinkClick} href={`/browse-timetables/program/${id}`}>
        {value}
      </Link>
    )
  }

  const tableColumns = [
    {
      name: 'programStudyTitle',
      label: t('Program Area'),
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: renderTitleLink,
      },
    },
  ]

  const rowsCount = size(dataTable)

  return (
    <Box>
      <DataTable
        className="data-table"
        title={''}
        data={dataTable}
        columns={tableColumns}
        options={{
          ...options,
          count: rowsCount,
        }}
      />
    </Box>
  )
}

export default ProgramStudiesTable
